// ------------------------------------------------------------------
// V1 API Request/Response Type
// ------------------------------------------------------------------
export const UserTypeTeacher = 0;
export const UserTypeStudent = 1;

export type loggingRequestType = {
  log_text: string; // ログ文字列
};

export type loggingResponseType = {
  log_text: string; // ログ文字列
};

export type userRequestType = {
  email: string;
  login_user_id?: number;
};

export type usersRequestType = {
  page_index?: number;  // 0起点（0が1ページ目）
  count_per_page?: number;  // ページ毎の表示データ数
  user_names?: string,  // 氏名の空白区切り
  student_numbers?: string, // 学籍番号の空白区切り
};

// portfolio画面用のリクエストタイプ
export type portfolioRequestType = {
  login_user_id?: number;   // userResponseType.id の値
  start_date?: string;      // yyyy/mm/dd形式
  end_date?: string;        // yyyy/mm/dd形式
  report_types?: number[];  // 0：週報、1：実習、2：省察、99：その他
  standard_tags?: string;   // 鳴門パースペクティブ（数値）の一覧 ※デリミタは半角スペース。数値は standardTagResponseType.id の値
  user_tags?: string;       // ハッシュタグの文字列 ※デリミタは半角スペース。
};

export type reportType = { id: number, value: string }

export type userResponseType = {
  id: number;
  email: string;
  user_name: string;
  nickname: string;
  user_type: number; // 0：教職員、1：学生
  student_number: string;
};

export type userReportsResponseType = {
  id: number; // 内部ID（デバッグ用）
  email: string;
  user_name: string;
  user_type: number;
  student_number: string;
  count_reports: number;
};

// 学生一覧画面 グラフ
export type usersResponseType = {
  total_count: number;
  content: Array<userReportsResponseType>;
};

// 鳴門パースペクティブ一覧
export type standardTagsResponseType = {
  content: Array<standardTagResponseType>;
};
export type standardTagResponseType = {
  id: number; // 内部ID（デバッグ用）
  tag_type: number;
  tag_name: string;
};

export type portfolioResponseType = {
  portfolioReportDataList: Array<any>;
  labels: Array<string>;
};

// ------------------------------------------------------------------
// V2 API Request/Response Type
// ・統合的LMS向け API
// ・エラーコードも返すように拡張した (generalResponseType.status)
// 　API呼び出し側は status の値を確認して、必要に応じてエラーメッセージ対応すること。
// ------------------------------------------------------------------

export enum academicDegreeCodeType {
  University = "540",  // 学部生
  Graduate1 = "600", // 大学院・修士課程
  Graduate2 = "610",
  Doctor = "620",
};

export enum languageType {
  Japanese = "ja", // 日本語
  English = "en",  // 英語
};

export type generalResponseType = {
  status: number; // 0:OK, 0以外はエラーコード
};

export type updateUserRequestType = {
  login_user_id: number;  // userResponseType.id の値
  nickname: string; // ニックネーム
};

// ポイント加算
export type loginUserRequestType = {
  email: string; // 検索対象のEメールアドレス
};

// 学籍情報画面向け
export type userInfoRequestType = {
  login_user_id: number;   // userResponseType.id の値
};

export type userInfoResponseType = {
  student_number: string;  // 学籍番号
  user_name: string;  // 氏名
  department_name: string;  // 学部/研究科
  major_name: string;  // 学科/専攻
  course_name: string;  // 課程/コース
  academic_degree_name: string;  // 学位/称号
  license_names: Array<string>;  // 教員免許
};

// 特記業績画面向け
export type userAchievementsRequestType = {
  login_user_id: number;   // userResponseType.id の値
};

export type updateUserAchievementsRequestType = {
  login_user_id: number;   // userResponseType.id の値
  achievements: Array<updateUserAchievementRequestType>;
};
export type updateUserAchievementRequestType = {
  achievement_type: string;  // 1, 2, ..., 8
  achievement_value: string;
};

// リンク画面向け
export type linkRequestType = {
  language: string;   // "ja" (日本語) or "en" (英語)
};

export type linkResponseType = {
  link_name: string; // リンクのラベル。画面に表示されるテキスト。
  link_url: string; // リンクのURL。
};

// ヘルプ画面向け
export type helpRequestType = {
  language: string;   // "ja" (日本語) or "en" (英語)
};

// 単位画面
export type creditRequestType = {
  login_user_id: number;   // userResponseType.id の値
};

export type creditResponseType = {
  previous_credits: creditPs8ResponseType;
  latest_credits: creditPs8ResponseType;
  base_credits: creditPs8ResponseType;
  period_count: number;
};
export type creditPs8ResponseType = {
  ps8_credit_1: number;
  ps8_credit_2: number;
  ps8_credit_3: number;
  ps8_credit_4: number;
  ps8_credit_5: number;
  ps8_credit_6: number;
  ps8_credit_7: number;
  ps8_credit_8: number;
};

// 単位画面(データ参照)
export type creditDataRefRequestType = {
  login_user_id: number;   // userResponseType.id の値
};

export type creditDataRefRowResponseType = {
  earned_period: number;  // 単位を修得した年
  earned_semester_code: string; // '1':前期, '2':後期
  subject_name: string; // 取得した単位の授業科目名。例「日本国憲法」
  credit_score: number; // 取得した単位の点数
  ps_credits: Array<number>;  // 鳴門パースペクティブの各観点毎の取得成績値。要素数は常に26個。
};

export type creditDataRefResponseType = {
  data_ref: Array<creditDataRefRowResponseType>;
};

// 育成指標画面
export type trainingIndicatorRequestType = {
  login_user_id: number;   // userResponseType.id の値
  target_type: string; // mext:文科省, tokushima:徳島県
};

export type trainingIndicatorResponseType = {
  training_indicators: Array<number>;
};

// 育成指標画面(データ参照)
export type trainingIndicatorDataRefRequestType = trainingIndicatorRequestType;

export type trainingIndicatorDataRefRowResponseType = {
  earned_period: number;  // 単位を修得した年
  earned_semester_code: string; // '1':前期, '2':後期
  subject_name: string; // 取得した単位の授業科目名。例「日本国憲法」
  credit_score: number; // 取得した単位の点数
  training_credits: Array<number>;  // 授業科目ごとの取得成績値。文科省の指標の場合は17個、徳島県の場合は20個。
};

export type trainingIndicatorDataRefResponseType = {
  data_ref: Array<trainingIndicatorDataRefRowResponseType>; // 要素数は常に26個（鳴門パースペクティブ）
};

// GPA画面
export type gpaRequestType = {
  login_user_id: number;   // userResponseType.id の値
  student_number?: string;
};

export type gpaResponseType = {
  scoreList: Array<gpaScoreListResponseType>;
  seriesNameList: Array<string>;
};

export type gpaScoreListResponseType = {
  earned_grade: number;
  earned_period: number;
  earned_semester_code: string;
  myself: number;
  // 仕様落ち all_students: number;
}

// GPA画面(データ参照)
export type gpaDataRefResponseType = {
  data_ref: Array<gpaDataRefRowResponseType>;
};

export type gpaDataRefRowResponseType = {
  user_id: string,
  subject_code: string,
  subject_name: string,
  credit: number,
  credit_score: number,
  earned_period: number,
  earned_semester_code: string,
  credit_grade_point: number | null
};

// N-CBT画面
// N-CBT画面(データ参照) も同様
export type NCBTRequestType = {
  login_user_id: number;   // userResponseType.id の値
};

export type NCBTResponseType = {
  scores: Array<any>;
};

// N-CBT画面(データ参照)
export type NCBTDataRefResponseType = {
  data_ref: any;
};

// SE・TS画面
export type SETSRequestType = {
  login_user_id: number;   // userResponseType.id の値
};

export type SETSResponseType = {
  scores: Array<SeTsScoreType>;
};

export type SeTsScoreType = {
  earned_period: number;
  earned_grade: number;
  graduation_status: number;
  se_survey_1: number;
  ts_survey_1: number;
  ts_survey_2: number;
  ts_survey_3: number;
  ts_survey_4: number;
  ts_survey_5: number;
  ts_survey_6: number;
  ts_survey_7: number;
  ts_survey_8: number;
  ts_survey_9: number;
  ts_survey_10: number;
  ts_survey_11: number;
  ts_survey_12: number;
  ts_survey_13: number;
};

// SE・TS画面(データ参照)
export type SETSDataRefResponseType = {
  data_ref: any;
};

// ディプロマサプリメント画面
export type diplomaSupplementRequestType = {
  login_user_id: number;   // userResponseType.id の値
  language: string;   // "ja" (日本語) or "en" (英語)
};

export type diplomaSupplementResponseType = {
  student_number: string; // 学籍番号
  user_name: string;  // 氏名
  department_name: string;  // 学部/研究科
  major_name: string;  // 学科/専攻
  course_name: string;  // 課程/コース
  academic_degree_code?: string;  // 学位 academicDegreeCodeType  
  academic_degree_name: string;  // 学位/称号
  achievement_1?: string; // 研究テーマ（空白区切り文字列）
  achievement_2?: string; // 受賞歴（空白区切り文字列）
  achievement_3?: string; // 資格（空白区切り文字列）
  achievement_4?: string; // 語学（空白区切り文字列）
  achievement_8?: string; // その他（空白区切り文字列）
  user_quality?: string;  // 学生の特性・強み・魅力
  user_self_refection?: string; // 自己評価・省察
  degree_issued_date: string; // 学位記発行日, 本証明日（YYYY年MM月DD日 形式）
  admission_date: string | null; // 入学年月日（YYYY年MM月DD日 形式）
  graduation_date: string | null; // 卒業年月日（YYYY年MM月DD日 形式）
};

export type updateDiplomaSupplementRequestType = {
  login_user_id: number;  // userResponseType.id の値
  user_self_refection?: string; // 自己評価・省察
};

// Chatbot画面
export type chatbotMessagesRequestType = {
  page_index: number;
  count_per_page: number;
  login_user_id: number;    // userResponseType.id の値
  start_date: string;       // 検索条件：開始日 yyyy/mm/dd形式
  search_words: string;     // 検索条件：単語 ※デリミタは半角スペース。
};

export type chatbotMessageResponseType = {
  id: number;   // 内部ID
  user_id: number;  // ユーザID
  msg_id: string;   // 一連の投稿を管理する MSG ID
  msg_datetime: string; // 投稿日時。yyyy-mm-dd hh:mm:ss形式
  role_type: number;  // 投稿ロール種類（0:ユーザ, 1:AI）
  msg_text: string; // 投稿内容
};

export type chatbotMessagesResponseType = {
  block_count: number;  // 一連の投稿を１ブロックとしたときのブロック数
  page_index: number; // 実際のページ位置 (常に >=0)
  content: Array<chatbotMessageResponseType>;
};

export type newChatbotMessageRequestType = {
  login_user_id: number;  // userResponseType.id の値
  msg_text: string;       // 会話本文
  // reset_and_newly: boolean; // 新しい会話を始める場合 true
};

export type chatbotLLMMessageRequestType = {
  login_user_id: number;  // userResponseType.id の値
};

export type updateChatbotMessageRequestType = {
  login_user_id: number;  // userResponseType.id の値
  msg_text: string;       // 会話本文
  msg_status?: number;
};
